// QR Kiosk v2 — KV2-8 guest PIN entry. A guest who has a machine PIN enters
// { machineInternalNumber, pinCode } → POST /v1/account/guest/redeem-pin.
// TWO machine modes:
//   • LOCKED (KV2-T1, machine-QR entry): `lockedMachine` supplied → the machine is taken
//     from the URL and shown DISPLAY-ONLY, never typed/editable (SEC-REQ-KV2G-3: the PIN
//     travels in the request body only; the machine number shown is not a secret).
//   • TYPED (magic-link/manual fallback): `prefillMachine` prefills the ?machine= query but
//     the field stays editable.
// On success the parent stores the token + enters the kiosk. Tokens + formAtoms only (no MUI).
import React, { useState } from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import { FLabel, FieldError, KTextInput, inputStyle } from "../reports/formAtoms";
import { usePressed } from "../shared/usePressed";
import { redeemPin, GuestRedeemResult, guestRedeemErrorKind } from "./guestAccessApi";

export interface GuestPinFormProps {
  prefillMachine?: string;
  /** Machine fixed from the QR URL — shown display-only, not typed/editable (KV2-T1). */
  lockedMachine?: string;
  onSuccess: (result: GuestRedeemResult) => void;
}

export function GuestPinForm({ prefillMachine, lockedMachine, onSuccess }: GuestPinFormProps) {
  const { translate } = useTranslation("public");
  const press = usePressed();

  const locked = !!lockedMachine;
  const [machine, setMachine] = useState(lockedMachine || prefillMachine || "");
  const [pin, setPin] = useState("");
  const [fieldErr, setFieldErr] = useState<"machine" | "pin" | null>(null);
  const [serverErr, setServerErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setServerErr(null);
    const m = (locked ? lockedMachine || "" : machine).trim();
    const p = pin.trim();
    if (!m) {
      setFieldErr("machine");
      return;
    }
    if (!p) {
      setFieldErr("pin");
      return;
    }
    setFieldErr(null);
    setBusy(true);
    try {
      const result = await redeemPin(m, p);
      onSuccess(result);
    } catch (err) {
      const kind = guestRedeemErrorKind(err);
      setServerErr(
        kind === "rateLimited"
          ? translate("kioskV2GuestRateLimitedHint")
          : kind === "network"
          ? translate("kioskV2GuestNetworkHint")
          : translate("kioskV2GuestInvalidPin")
      );
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {locked ? (
        <div>
          <FLabel>{translate("kioskV2GuestMachineLabel")}</FLabel>
          <div
            style={{
              ...inputStyle(false),
              display: "flex",
              alignItems: "center",
              background: "var(--bg-soft)",
              fontFamily: "var(--font-mono)",
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: "0.03em",
              color: "var(--ink)",
            }}
          >
            #{lockedMachine}
          </div>
        </div>
      ) : (
        <div>
          <FLabel req>{translate("kioskV2GuestMachineLabel")}</FLabel>
          <KTextInput
            inputMode="numeric"
            autoComplete="off"
            value={machine}
            error={fieldErr === "machine"}
            placeholder={translate("kioskV2GuestMachinePlaceholder")}
            onChange={(e) => setMachine(e.target.value)}
          />
          {fieldErr === "machine" && <FieldError>{translate("kioskV2GuestPinMachineRequired")}</FieldError>}
        </div>
      )}

      <div>
        <FLabel req>{translate("kioskV2GuestPinLabel")}</FLabel>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={pin}
          placeholder={translate("kioskV2GuestPinPlaceholder")}
          onChange={(e) => setPin(e.target.value)}
          style={{
            ...inputStyle(fieldErr === "pin"),
            fontFamily: "var(--font-mono)",
            fontSize: 20,
            letterSpacing: "0.28em",
            textAlign: "center",
          }}
        />
        {fieldErr === "pin" && <FieldError>{translate("kioskV2GuestPinRequired")}</FieldError>}
      </div>

      {serverErr && <FieldError>{serverErr}</FieldError>}

      <button
        type="submit"
        {...press.pressHandlers}
        disabled={busy}
        style={{
          height: 52,
          borderRadius: 12,
          border: "1px solid var(--tier-accent)",
          background: "var(--tier-accent)",
          color: "var(--tier-accent-fg)",
          fontFamily: "inherit",
          fontSize: 15.5,
          fontWeight: 700,
          cursor: busy ? "default" : "pointer",
          opacity: busy ? 0.7 : 1,
          ...press.pressStyle,
        }}
      >
        {busy ? translate("kioskV2GuestVerifying") : translate("kioskV2GuestPinSubmit")}
      </button>
    </form>
  );
}

export default GuestPinForm;
